import React, { Component } from 'react';
import initialState from '../initialState';
import appSettings from '../settings/appSettings';
import AppInactive from './AppInactive';
import AppLoad from './AppLoad';
import AppActive from './AppActive';
import AppError from './AppError';
import appErrorWrapper from './AppErrorWrapper';
import { getChildByChildGUID } from 'rcds-client';
import { getQueryStringParams, parseDate, getMonthName, isSignIn, delay, replacePronouns } from '../helpers/helpers';
import { mapMediaData, mergeMediaData, getMediaContentType } from '../helpers/mediaData';

export class App extends Component {
  constructor() {
    super();
    this.state = this.getInitialState();
    this.setAppStatus = this.setAppStatus.bind(this);
    this.appSettings = appSettings;
    this.getChildData = this.getChildData.bind(this);
    this.getRFLData = this.getRFLData.bind(this);
  }

  componentDidMount() {
    this.initApp();
  }

  initApp() {
    const params = this.getQueryParams();

    if (!params) {
      this.setAppStatus("error");
      return;
    }

    this.getAppData(params);
  }

  setAppStatus(status, message = null) {
    this.setState((prevState) => {
      prevState.app.status = status;

      if (message !== null) {
        prevState.app.message = message;
      }

      return prevState;
    });
  }

  setParamValues({ ID, rflgrouping, emailallowed }) {
    this.setState((prevState) => {
      prevState.data.childGuid = ID;
      prevState.data.rflgrouping = rflgrouping;
      prevState.data.emailallowed = emailallowed;
      return prevState;
    });
  }

  setChildData(childData) {
    this.setState((prevState) => {
      prevState.data.childData = childData;
      return prevState;
    });
  }

  setMediaData(mediaData = {}) {
    this.setState((prevState) => {
      prevState.data.mediaData = mediaData;
      return prevState;
    });
  }

  getQueryParams() {
    const params = getQueryStringParams();

    const { ID = "", rflgrouping = "", emailallowed = false } = params;
    if (!ID) {
      return null;
    }
    this.setParamValues({ ID, rflgrouping, emailallowed });
    return { ID, rflgrouping, emailallowed };
  }

  getAppData({ ID, rflgrouping, emailallowed }) {
    this.setAppStatus("load");
    const wvRCDS = {
      apiKey: window.WVUS_RCDS_VARS.apiKey || "19a586b4497846d1bbcad6fe3e0de302",
      url: window.WVUS_RCDS_VARS.url || "https://wv-rcds-prd-api.azure-api.net/v3",
    };

    return delay(1000)
      .then(() => {
        return getChildByChildGUID(ID, wvRCDS);
      })
      .then((resp) => {
        const { data } = resp;
        return data;
      })
      .then((childData) => {
        this.setChildData(childData);
        return childData;
      })
      .then((childData) => {
        const { rflgrouping } = this.state.data;
        const { emailallowed } = this.state.data;
        const { featuredVideoMap = {} } = appSettings;
        const featuredVideoContentType =
          (featuredVideoMap[rflgrouping] || {}).contentTypeCode || "";
        let mediaFilters =
          (featuredVideoContentType && [
            { contentTypeCode: featuredVideoContentType },
          ]) ||
          [];
        // Filter out ST002 - Children's Special Moments videos
        mediaFilters.push({ subtypeCode: "ST002" });

        //WebPublishable
        const isAuth = isSignIn();
        const { webPublishableFlag, webPublishableRestrictionsFlag } =
          childData;
        const isWebpublishable =
          (webPublishableFlag && isAuth) ||
          (webPublishableFlag && !webPublishableRestrictionsFlag);

        const mappedChildMedia = isWebpublishable
          ? mapMediaData(childData.childMedia, mediaFilters, "child")
          : {};

        const mappedProjectMedia = mapMediaData(
          childData.projectMedia,
          mediaFilters,
          "project"
        );

        const mappedAllMedia = mergeMediaData(
          mappedChildMedia,
          mappedProjectMedia
        );

        // Find Featured Media
        const featuredVideoMedia =
          (mediaFilters.length &&
            getMediaContentType(
              childData.projectMedia, // MediaData
              featuredVideoContentType, // ContentTypeCode
              "web", // format
              "mediaDate", // Sort By
              "DESC" // DESC or ASC
            )) ||
          [];

        this.setMediaData({
          childMedia: mappedChildMedia,
          projectMedia: mappedProjectMedia,
          featuredVideoMedia: featuredVideoMedia,
          allMedia: mappedAllMedia,
        });

        this.setAppStatus("active");
      })
      .catch((err) => {
        // TODO: Log Error
        console.error(err);
        this.setAppStatus("error");
      });
  }

  getInitialState() {
    return initialState;
  }

  mapChildData(childData) {
    const { silhouette } = appSettings;
    const {
      birthdate,
      identifier,
      childMedia,
      gender,
      webPublishableFlag,
      webPublishableRestrictionsFlag,
    } = childData;
    const extraChildData = {};
    const isAuth = isSignIn();
    const extractChildMedia = (childMedia, contentTypeCode, format) => {
      const matchedContentTypes = childMedia.filter((item) => {
        return (
          item.contentTypeCode &&
          item.contentTypeCode.toUpperCase() === contentTypeCode.toUpperCase()
        );
      });

      // Prioritize CGPs with no subtype (empty subtypeCode)
      const defaultCGPs = matchedContentTypes.filter((item) => {
        return !item.subtypeCode || item.subtypeCode === "";
      });

      // Use default CGPs if available, otherwise fall back to all matched CGPs
      const prioritizedCGPs = defaultCGPs.length > 0 ? defaultCGPs : matchedContentTypes;

      const matchedFormats = prioritizedCGPs.filter((item) => {
        return (
          item.format && item.format.toUpperCase() === format.toUpperCase()
        );
      });

      const selectedMediaItem =
        matchedFormats[0] || prioritizedCGPs[0] || {};

      return selectedMediaItem;
    };

    const isWebpublishable =
      (webPublishableFlag && isAuth) ||
      (webPublishableFlag && !webPublishableRestrictionsFlag);

    const birthDateParsed = (birthdate && parseDate(birthdate)) || "";
    const birthMonth = (birthDateParsed && birthDateParsed.month) || "";
    const birthDay = (birthDateParsed && birthDateParsed.day) || "";
    const birthYear = (birthDateParsed && birthDateParsed.year) || "";
    const birthMonthName =
      (birthDay && getMonthName(birthDateParsed.month)) || "";
    const childGender =
      (gender && (gender.toLowerCase() === "male" ? "boy" : "girl")) || "";
    const selectedMedia = extractChildMedia(childMedia, "CGP", "web");
    const cgpFallBack = (gender && silhouette[gender]) || silhouette.Male;

    extraChildData.isAuth = isAuth;
    extraChildData.guid = identifier;
    extraChildData.birthMonth = birthMonth;
    extraChildData.birthDay = birthDay;
    extraChildData.birthYear = birthYear;
    extraChildData.birthMonthName = birthMonthName;
    extraChildData.childGender = childGender;
    extraChildData.isWebpublishable = isWebpublishable;
    extraChildData.cgpWebUrl =
      (isWebpublishable && selectedMedia.url) || cgpFallBack;

    return Object.assign({}, childData, extraChildData);
  }

  getRFLData() {
    const { rflgrouping, childData } = this.state.data;
    const { givenName, gender } = childData;
    const { rflGroups, replaceTokens } = appSettings;
    const childNameToken = replaceTokens.childname || "";
    const replaceWithChildName = (str, token, val) => {
      return str.replace(new RegExp(token, "g"), val);
    };
    const defaultText = ""; // Need Default Text
    const defaultTitle = ""; // Need Default Text

    const key = rflgrouping;
    const rflgroup = rflGroups[key] || {
      title: "",
      text: "",
    };

    let rflText = replaceWithChildName(rflgroup.text || defaultText, childNameToken, givenName);

    rflText = replacePronouns(rflText, gender, replaceTokens);

    let rflTitle = replaceWithChildName(rflgroup.title || defaultTitle, childNameToken, givenName);

    rflTitle = replacePronouns(rflTitle, gender, replaceTokens);

    return {
      group: rflgrouping,
      text: rflText,
      title: rflTitle,
    };
  }

  getChildData() {
    const { childData } = this.state.data;
    const { mapChildData } = this;

    return mapChildData(childData);
  }

  getMediaData() {
    const { mediaData = {} } = this.state.data;

    return mediaData;
  }

  render() {
    const { app } = this.state;
    const { status, error, message } = app;

    switch (status) {
      case "inactive":
        return <AppInactive timeout={20 * 1000} setAppStatus={this.setAppStatus} />;
      case "load":
        return <AppLoad timeout={30 * 1000} setAppStatus={this.setAppStatus} />;
      case "active":
        return (
          <AppActive
            appData={this.state.data}
            childData={this.getChildData()}
            mediaData={this.getMediaData()}
            rflData={this.getRFLData()}
          />
        );
      default:
        return <AppError />;
    }
  }
}

export default appErrorWrapper(App);
